import { useState } from "react";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "~/components/Tooltip";
import { Checkbox } from "~/components/ui/checkbox";
import {
    AssetTimeseriesColumns,
    AssetTimeseriesGroupBy,
} from "~/modules/asset_timeseries_v4/types";
import { FilterOperators } from "~/modules/filters/types";
import {
    MetricTypes as YieldMetricTypes,
    YieldTimeseriesChart,
} from "~/modules/visualization/components/YieldTimeseriesChart";
import { RWA_COLORS } from "~/styles/colors";
import {
    TREASURY_YIELD_DISPLAY_START_DATE,
    TREASURY_YIELD_QUERY_START_DATE,
} from "./consts";
import { ASSET_CLASS_IDS } from "~/consts";
import { ReferenceRatesFetcher } from "~/modules/visualization/components/ReferenceRatesFetcher";

// // // //

const MIN_TREASURY_YIELD_CIRCULATING_VALUE = 100_000_000; // $100M

export function HomeTreasuryYieldChart() {
    const [showConstituents, setShowConstituents] = useState(false);

    return (
        <ReferenceRatesFetcher>
            {({ data: sofrSeries }) => (
                <YieldTimeseriesChart
                    title="Tokenized U.S. Treasury Yield"
                    infoTooltip={
                        showConstituents
                            ? "The blue line shows the equal-weighted average daily 7-day APY across tokenized U.S. Treasury assets with more than $100 million in current circulating asset value. Muted gray lines show the underlying assets. The average is smoothed using a trailing 7-calendar-day simple moving average. Data is shown from January 1, 2024."
                            : "Daily 7-day APY is averaged across tokenized U.S. Treasury assets with more than $100 million in current circulating asset value, then smoothed using a trailing 7-calendar-day simple moving average. Data is shown from January 1, 2024."
                    }
                    footer={
                        <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground/80">
                            <span>
                                SOFR data source: Federal Reserve Bank of New
                                York.
                            </span>
                            <Tooltip
                                placement="top"
                                tooltipContent={
                                    <p className="max-w-xs px-3 py-2 text-sm">
                                        The Secured Overnight Financing Rate
                                        (SOFR) data is subject to the Terms of
                                        Use posted at newyorkfed.org. The New
                                        York Fed is not responsible for
                                        publication of the SOFR data by RWA.xyz,
                                        does not sanction or endorse this
                                        republication, and has no liability for
                                        your use.
                                    </p>
                                }
                            >
                                {({ setTriggerRef }) => (
                                    <button
                                        ref={setTriggerRef}
                                        type="button"
                                        aria-label="SOFR data terms of use"
                                        className="inline-flex size-6 cursor-help items-center justify-center rounded-full transition-colors hover:text-foreground focus-visible:text-foreground focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary"
                                    >
                                        <FontAwesomeIcon
                                            icon={faInfoCircle}
                                            className="size-3"
                                        />
                                    </button>
                                )}
                            </Tooltip>
                        </div>
                    }
                    headerControls={
                        <label
                            htmlFor="treasury-yield-show-constituents"
                            className="flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap text-base font-normal text-muted-foreground transition-colors select-none hover:text-foreground/80 sm:text-sm"
                        >
                            <Checkbox
                                id="treasury-yield-show-constituents"
                                name="treasury-yield-show-constituents"
                                checked={showConstituents}
                                onCheckedChange={(checked) =>
                                    setShowConstituents(checked === true)
                                }
                                // NOTE - larger tap target on mobile
                                className="size-5 sm:size-4"
                                data-testid="treasury-yield-show-constituents"
                            />
                            Show Constituents
                        </label>
                    }
                    additionalSeries={sofrSeries}
                    dataSeriesName="Tokenized U.S. Treasury Yield"
                    dataSeriesColor={RWA_COLORS.blue[800]}
                    displayStartDate={TREASURY_YIELD_DISPLAY_START_DATE}
                    trailingAverageDays={7}
                    lineWidth={1.5}
                    hideLegend
                    showAverageOnly={!showConstituents}
                    showAverageWithConstituents={showConstituents}
                    showControls={false}
                    analyticsChartName="home-treasury-yield"
                    groupBy={AssetTimeseriesGroupBy.assetTicker}
                    filters={[
                        {
                            field: AssetTimeseriesColumns.assetTicker,
                            operator: FilterOperators.IsNotNull,
                            value: null,
                        },
                        {
                            field: AssetTimeseriesColumns.assetClassID,
                            operator: FilterOperators.Equals,
                            value: ASSET_CLASS_IDS.TreasuryProduct,
                        },
                        {
                            field: AssetTimeseriesColumns.circulatingAssetValueDollar,
                            operator: FilterOperators.GreaterThanOrEqual,
                            value: MIN_TREASURY_YIELD_CIRCULATING_VALUE,
                        },
                        {
                            field: AssetTimeseriesColumns.date,
                            operator: FilterOperators.OnOrAfter,
                            value: TREASURY_YIELD_QUERY_START_DATE,
                        },
                    ]}
                    controls={{
                        legend: {
                            enabled: true,
                            active: true,
                            align: "center",
                            layout: "horizontal",
                            verticalAlign: "bottom",
                            itemWidth: 120,
                        },
                        metrics: {
                            options: [YieldMetricTypes.Apy7Day],
                        },
                        navigator: {
                            enabled: false,
                            active: false,
                        },
                    }}
                    columnSpanConfig={{
                        sm: 12,
                        md: 12,
                        lg: 12,
                        xl: 6,
                        xxl: 6,
                    }}
                />
            )}
        </ReferenceRatesFetcher>
    );
}
